import styles from "./Cart.module.css";
import { useTranslation } from "react-i18next";
import { formatCurrency, formatCurrencyFromCzk } from "../../utils/prices";
import { czk_eur_rate } from "../../constants/prices";
import { timeToMinutes } from "../../utils/calendar";
import { getCartTotalsFromItems, blockHasSvoz, COLLECTION_FEE_CZK } from "../../utils/cart";
import type { CartPriceSummaryCurrency } from "../../utils/cart";
import type { CartData, CartItem } from "../../types/cart.types";
import CartSummaryActions from "./CartSummaryActions";

type CartSummaryProps = {
    isDesktop: boolean;
    groupedCart: Record<string, Record<string, CartItem[]>>;
    cartData: CartData | null;
    orderType: string;
    selectedCurrency: string;
    blockLookup: {
        sales: any[];
        packages: any[];
        storage: any[];
    };
    transportEx: boolean;
    priceSummaryCurrency: CartPriceSummaryCurrency;
    onDropCart: () => void;
    onCheckout: () => void;
    allBlocksEnough: boolean;
};

const CartSummary = ({
    isDesktop,
    groupedCart,
    cartData,
    orderType,
    selectedCurrency,
    blockLookup,
    transportEx,
    priceSummaryCurrency,
    onDropCart,
    onCheckout,
    allBlocksEnough,
}: CartSummaryProps) => {
    const { t } = useTranslation();

    const sections = Object.entries(groupedCart ?? {})
        .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime())
        .flatMap(([date, times]) =>
            Object.entries(times)
                .sort(
                    ([timeA], [timeB]) =>
                        timeToMinutes(timeA.split("-")[0]) - timeToMinutes(timeB.split("-")[0])
                )
                .map(([time, products]) => ({ date, time, products }))
        );

    if (isDesktop) {
        return (
            <>
                <div className={styles.CartSummary}>
                    <div className={styles.CartTitleSecondary}>
                        <p>
                            {orderType === "demand"
                                ? t("cart.summary_section.demand_title")
                                : t("cart.summary_section.title")}
                        </p>
                    </div>
                    <div className={styles.CartSummaryPcContent}>
                        <div className={styles.SummaryBox}>
                            {sections.map(({ date, time, products }) => {
                                // Use the same per-line pricing as the grand total
                                // (getCartTotalsFromItems) so block subtotals reconcile
                                // exactly with "Cena bez DPH".
                                const blockPriceCZK = getCartTotalsFromItems({
                                    [`${date}_${time}`]: products,
                                }).withoutTax;
                                const delivery = cartData?.delivery_item;
                                const hasSvoz = blockHasSvoz(products, blockLookup);
                                const svozCzk = hasSvoz ? COLLECTION_FEE_CZK : 0;

                                return (
                                    <div key={`${date}-${time}`} className={styles.SummaryItem}>
                                        <p className={styles.SummaryItemTitle}>
                                            {date}, {time}
                                        </p>
                                        <div className={styles.SummaryItemBlock}>
                                            <p className={styles.SummaryItemTextSmall}>
                                                {t("cart.summary_section.items")}
                                            </p>
                                            <p className={styles.SummaryItemTextSmall}>
                                                {t("cart.summary_section.no_dph_short")}{" "}
                                                {formatCurrencyFromCzk(
                                                    blockPriceCZK,
                                                    czk_eur_rate,
                                                    selectedCurrency
                                                )}{" "}
                                            </p>
                                        </div>
                                        <div className={styles.SummaryItemBlock}>
                                            <p className={styles.SummaryItemTextSmall}>
                                                {delivery?.name?.trim()
                                                    ? delivery.name
                                                    : t("cart.summary_section.delivery")}
                                            </p>
                                            <p className={styles.SummaryItemTextSmall}>
                                                {t("cart.summary_section.no_dph_short")}{" "}
                                                {formatCurrencyFromCzk(
                                                    delivery?.price_without_tax ?? 0,
                                                    czk_eur_rate,
                                                    selectedCurrency
                                                )}{" "}
                                            </p>
                                        </div>
                                        {hasSvoz && (
                                            <div className={styles.SummaryItemBlock}>
                                                <p className={styles.SummaryItemTextSmall}>
                                                    {t("cart.summary_section.collection")}
                                                </p>
                                                <p className={styles.SummaryItemTextSmall}>
                                                    {t("cart.summary_section.no_dph_short")}{" "}
                                                    {formatCurrencyFromCzk(
                                                        svozCzk,
                                                        czk_eur_rate,
                                                        selectedCurrency
                                                    )}{" "}
                                                </p>
                                            </div>
                                        )}
                                        <div className={styles.SummaryItemBlock}>
                                            <p className={styles.SummaryItemTextBig}>
                                                {t("cart.summary_section.total")}
                                            </p>
                                            <p className={styles.SummaryItemTextBig}>
                                                {formatCurrencyFromCzk(
                                                    blockPriceCZK +
                                                        (delivery?.price_without_tax ?? 0) +
                                                        svozCzk,
                                                    czk_eur_rate,
                                                    selectedCurrency
                                                )}{" "}
                                                {t("cart.summary_section.no_dph_short")}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className={styles.SummaryContent}>
                            <div className={styles.OrderSummary}>
                                <p className={styles.OrderSummaryTitle}>
                                    {t("cart.summary_section.total")}
                                </p>
                                <div className={styles.OrderSummaryContent}>
                                    <div className={styles.PriceSummaryNoDph}>
                                        <p>{t("cart.summary_section.price_without_dph")}</p>
                                        <p>
                                            {formatCurrency(
                                                priceSummaryCurrency.totalNoTax,
                                                selectedCurrency
                                            )}
                                        </p>
                                    </div>
                                    <div className={styles.PriceSummaryDph}>
                                        <p>{t("cart.summary_section.price_with_dph")}</p>
                                        <p>
                                            {formatCurrency(
                                                priceSummaryCurrency.totalWithTax,
                                                selectedCurrency
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <div className={styles.CartSummary}>
            <div className={styles.CartSummaryContent}>
                <div className={styles.OrderSummary}>
                    <div className={styles.OrderSummaryContent}>
                        <div className={styles.SummaryPart}>
                            <p className={styles.SummaryTitle}>
                                {orderType === "demand"
                                    ? t("cart.summary_section.demand_title")
                                    : t("cart.summary_section.title")}
                            </p>
                        </div>
                        <div className={styles.SummaryPart}>
                            <p className={styles.SummarySubtitle}>
                                {t("cart.summary_section.total")}
                            </p>
                        </div>
                        <div className={styles.SummaryPart}>
                            <p className={styles.SummaryItem}>
                                {t("cart.summary_section.price_without_dph")}
                            </p>
                            <p>
                                {formatCurrency(
                                    priceSummaryCurrency.totalNoTax,
                                    selectedCurrency
                                )}
                            </p>
                        </div>
                        <div className={styles.SummaryPart}>
                            <p className={styles.SummaryItem}>
                                {t("cart.summary_section.price_with_dph")}
                            </p>
                            <p>
                                {formatCurrency(
                                    priceSummaryCurrency.totalWithTax,
                                    selectedCurrency
                                )}
                            </p>
                        </div>
                        {transportEx && (
                            <div className={styles.SummaryPart}>
                                <p className={styles.SummaryItem}>
                                    {t("cart.summary_section.collection")}
                                </p>
                                <p className={styles.SummaryItem}>
                                    {formatCurrency(
                                        priceSummaryCurrency.collection,
                                        selectedCurrency
                                    )}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                <div className={styles.OrderInfo}>
                    <div className={styles.CartOrderPart}>
                        <p className={styles.OrderPriceTitle}>
                            {t("cart.summary_section.subtotal")}
                        </p>
                        <div className={styles.OrderPricePart}>
                            <p className={styles.OrderPriceNoDph}>
                                {t("cart.summary_section.no_dph_short")}{" "}
                                {formatCurrency(
                                    priceSummaryCurrency.baseNoTax,
                                    selectedCurrency
                                )}
                            </p>
                            <p className={styles.OrderPriceDph}>
                                {t("cart.summary_section.dph_short")}{" "}
                                {formatCurrency(
                                    priceSummaryCurrency.baseWithTax,
                                    selectedCurrency
                                )}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <CartSummaryActions
                className={styles.CheckoutButton}
                onDropCart={onDropCart}
                onCheckout={onCheckout}
                disabled={!allBlocksEnough}
            />
        </div>
    );
};

export default CartSummary;
